import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { uid, formatMontant } from '../../utils/helpers';
import { parseBFCStatement, matchAdherent, TYPE_CONFIG, CATEGORIES } from './comptaUtils';

const pdfjsLib = window.pdfjsLib;
pdfjsLib.GlobalWorkerOptions.workerSrc =
  'https://unpkg.com/pdfjs-dist@3.11.174/legacy/build/pdf.worker.min.js';

// ─── Sélecteur adhérent avec autocomplétion ─────────────────────────────────────────────
function AdherentPicker({ value, adherents, onChange, placeholder = '🔍 Rechercher…' }) {
  const [inputVal,    setInputVal]    = useState('');
  const [open,        setOpen]        = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const wrapRef  = useRef();

  const selected    = value ? adherents.find(a => a.id === value) : null;
  const displayName = selected ? `${selected.nom} ${selected.prenom}` : '';

  const suggestions = useMemo(() => {
    const norm = s => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase().replace(/\s+/g, ' ').trim();
    const q = norm(inputVal);
    const list = q
      ? adherents.filter(a => {
          const full = norm(`${a.nom} ${a.prenom}`);
          const rev  = norm(`${a.prenom} ${a.nom}`);
          return full.includes(q) || rev.includes(q);
        })
      : adherents;
    return list.slice(0, 10);
  }, [adherents, inputVal]);

  useEffect(() => {
    const h = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const selectAdherent = id => { onChange(id); setInputVal(''); setOpen(false); };

  const handleKeyDown = e => {
    if (!open) { if (e.key === 'ArrowDown') setOpen(true); return; }
    if (e.key === 'ArrowDown') { setHighlighted(h => Math.min(h+1, suggestions.length-1)); e.preventDefault(); }
    else if (e.key === 'ArrowUp')  { setHighlighted(h => Math.max(h-1, 0)); e.preventDefault(); }
    else if (e.key === 'Enter' && suggestions[highlighted]) { selectAdherent(suggestions[highlighted].id); e.preventDefault(); }
    else if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div ref={wrapRef} style={{ position:'relative', flex:1, minWidth:150 }}>
      <input className="form-control"
        style={{ fontSize:11, padding:'3px 6px', width:'100%' }}
        placeholder={placeholder}
        value={open || !selected ? inputVal : displayName}
        onFocus={() => { setOpen(true); setInputVal(''); setHighlighted(0); }}
        onChange={e => { setInputVal(e.target.value); onChange(null); setHighlighted(0); }}
        onKeyDown={handleKeyDown}
      />
      {open && suggestions.length > 0 && (
        <div style={{
          position:'absolute', top:'100%', left:0, right:0, zIndex:2000,
          background:'#fff', border:'1px solid #e5e7eb', borderRadius:6,
          boxShadow:'0 4px 16px rgba(0,0,0,0.15)', maxHeight:200, overflowY:'auto',
        }}>
          {suggestions.map((a, i) => (
            <div key={a.id}
              onMouseDown={e => { e.preventDefault(); selectAdherent(a.id); }}
              style={{
                padding:'6px 10px', fontSize:11, cursor:'pointer',
                background: i === highlighted ? '#eff6ff' : '#fff',
                borderBottom:'1px solid #f3f4f6',
              }}>
              {a.nom} {a.prenom}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const FILTER_OPTIONS = [
  { key: 'all', label: 'Tout' },
  { key: 'A',   label: '💰 Cotisations' },
  { key: 'B',   label: '❌ Impayés' },
  { key: 'C',   label: '📦 Remises SEPA' },
  { key: 'D',   label: '🏦 Frais' },
  { key: 'E',   label: '🛒 Dépenses' },
  { key: 'F',   label: '🔄 Reversements' },
];

const PREFIXES_BFC = /^(VIREMENT\s+SEPA\s+RECU?\s*|VIR\s+SEPA\s+RECU?\s*|VIREMENT\s+RECU?\s*|VIR\s+RECU?\s*|IMPAYE\s+PRLV\s*SEPA\s*|IMPAYE\s+PRELEVEMENT\s*|IMPAYE\s+|REMISE\s+PRLV\s*SEPA\s*|PRLV\s+SEPA\s*)/i;
const parseNomFromLibelle = (lib) => {
  const cleaned = (lib || '').replace(PREFIXES_BFC, '').replace(/\s+/g, ' ').trim();
  const parts = cleaned.split(/\s+/);
  if (parts.length >= 2) return { nom: parts[0], prenom: parts.slice(1, 3).join(' ') };
  return { nom: cleaned, prenom: '' };
};

export default function OngletImport({
  adherents, etablissements, selectedYear, activeMois, setActiveMois,
  comptabilite, saveComptaOperation, setComptaMoisData, clearComptaMoisOperations,
  setCotisation, addJournal, saveAdherent,
}) {
  const [ops, setOps]           = useState([]);
  const [processing, setProc]   = useState(false);
  const [error, setError]       = useState('');
  const [isDrag, setIsDrag]     = useState(false);
  const [filter, setFilter]     = useState('all');
  const [validated, setValidated] = useState('');
  const [pendingFile, setPendingFile]  = useState(null);
  const [parsedSolde, setParsedSolde]  = useState(null);
  const [coherence, setCoherence]      = useState(null);
  const [createModal, setCreateModal]  = useState(null);
  const fileRef = useRef();

  // ── Existing ops for selected month ──
  const existingOps = useMemo(() => {
    return (comptabilite[selectedYear]?.[activeMois]?.operations) || [];
  }, [comptabilite, selectedYear, activeMois]);

  // ── Process PDF ──
  const processPDF = useCallback(async (file) => {
    setProc(true); setError(''); setOps([]); setFilter('all'); setValidated(''); setCoherence(null);
    try {
      const ab = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: new Uint8Array(ab) }).promise;
      let fullText = '';
      for (let p = 1; p <= pdf.numPages; p++) {
        const page = await pdf.getPage(p);
        const tc = await page.getTextContent();
        const rows = [];
        for (const item of tc.items) {
          if (!item.str.trim()) continue;
          const y = Math.round(item.transform[5] / 4) * 4;
          const r = rows.find(r => r.y === y);
          if (r) r.parts.push({ x: item.transform[4], text: item.str });
          else rows.push({ y, parts: [{ x: item.transform[4], text: item.str }] });
        }
        rows.sort((a, b) => b.y - a.y);
        for (const row of rows) {
          row.parts.sort((a, b) => a.x - b.x);
          fullText += row.parts.map(p => p.text).join(' ') + '\n';
        }
      }

      const { ops: rawParsed, soldeOuverture, soldeFinal, totalDebits_PDF, totalCredits_PDF } = parseBFCStatement(fullText, uid);
      if (soldeFinal !== null) setParsedSolde(soldeFinal);
      const parsed = rawParsed;

      // ── Contrôle de cohérence solde BFC ──
      const totalCreditsCalc = rawParsed.reduce((s, o) => s + (o.credit || 0), 0);
      const totalDebitsCalc  = rawParsed.reduce((s, o) => s + (o.debit  || 0), 0);
      if (soldeOuverture !== null && soldeFinal !== null) {
        const soldeCalc = Math.round((soldeOuverture + totalCreditsCalc - totalDebitsCalc) * 100) / 100;
        const ecart     = Math.round((soldeCalc - soldeFinal) * 100) / 100;
        setCoherence({ soldeOuverture, soldeFinal, soldeCalc, totalCreditsCalc, totalDebitsCalc, totalDebits_PDF, totalCredits_PDF, ecart });
      } else {
        setCoherence(null);
      }

      // Apply fuzzy matching
      const matched = parsed.map(op => {
        const amount = op.credit > 0 ? op.credit : op.debit;
        const { status, adherentId, score, reasons } = matchAdherent(op.libelleBrut, op.type, adherents, amount);
        return {
          ...op,
          adherentId,
          matchStatus: status,
          matchScore: score,
          matchReasons: reasons || [],
          pointe: status === 'auto' && ['A','B'].includes(op.type),
        };
      });

      setOps(matched); setCoherence(prev => prev);
      setComptaMoisData(selectedYear, activeMois, {
        dateImport: new Date().toISOString(),
        importedFile: file.name,
        statut: 'importe',
        ...(soldeOuverture !== null && { soldeOuverture }),
        ...(soldeFinal     !== null && { soldeReleve: soldeFinal }),
      });

      if (!matched.length)
        setError('Aucune opération détectée. Vérifiez que le fichier est un relevé BFC OI.');
      else if (!rawParsed.length) setCoherence(null);
    } catch (e) {
      setError(`Erreur lecture PDF : ${e.message}`);
    } finally {
      setProc(false);
    }
  }, [adherents, selectedYear, activeMois, setComptaMoisData]);

  const handleFileChange = e => {
    const f = e.target.files[0]; if (!f) return; e.target.value = '';
    if (existingOps.length > 0) setPendingFile(f); else processPDF(f);
  };
  const handleDrop = e => {
    e.preventDefault(); setIsDrag(false);
    const f = e.dataTransfer.files[0];
    if (!f) return;
    if (f.type !== 'application/pdf') { setError('Fichier PDF requis.'); return; }
    if (existingOps.length > 0) setPendingFile(f); else processPDF(f);
  };

  // ── Inline update ──
  const updateOp = (id, patch) => setOps(prev => prev.map(o => o.id === id ? { ...o, ...patch } : o));

  // ── Créer adhérent depuis import ──
  const openCreateModal = (op) => {
    const { nom, prenom } = parseNomFromLibelle(op.libelleBrut);
    setCreateModal({ opId: op.id, nom, prenom, etabId: '', montant: 0 });
  };
  const handleCreateAdherent = () => {
    if (!createModal || !saveAdherent) return;
    const { opId, nom, prenom, etabId, montant } = createModal;
    const newId = uid();
    const newAdh = {
      id: newId,
      nom: nom.trim().toUpperCase(),
      prenom: prenom.trim(),
      statut: 'Actif',
      etablissementId: etabId || null,
      montant: parseFloat(montant) || 0,
      dateAdhesion: new Date().toISOString().slice(0, 10),
    };
    saveAdherent(newAdh);
    updateOp(opId, { adherentId: newId, matchStatus: 'approx', _forceSelect: false, pointe: true });
    setCreateModal(null);
  };

  // ── Validate ──
  const handleValidate = () => {
    const toSave = ops.filter(o => o.pointe);
    toSave.forEach(op => {
      saveComptaOperation(selectedYear, activeMois, { ...op, validated: true, statut: 'valide' });
    });

    // Point cotisations for type A
    const month = activeMois;
    ops.filter(o => o.pointe && o.type === 'A' && o.adherentId).forEach(op => {
      setCotisation(selectedYear, op.adherentId, month, 'paye');
    });
    // Mark defaut for type B
    ops.filter(o => o.pointe && o.type === 'B' && o.adherentId).forEach(op => {
      setCotisation(selectedYear, op.adherentId, month, 'defaut_provision');
    });

    addJournal && addJournal('comptabilite',
      `Import relevé ${MOIS_L[activeMois-1]} ${selectedYear} : ${toSave.length} opération(s) validée(s)`);
    setValidated(`✅ ${toSave.length} opération(s) enregistrée(s) pour ${MOIS_L[activeMois-1]} ${selectedYear}`);
    setOps(prev => prev.map(o => o.pointe ? { ...o, validated: true } : o));
  };

  const monthInfo = comptabilite[selectedYear]?.[activeMois];

  // ── KPI ──
  const soldeAffiche = parsedSolde ?? monthInfo?.soldeReleve ?? null;

  const kpi = useMemo(() => {
    const all = ops.length > 0 ? ops : existingOps;
    return {
      cotisations: all.filter(o => ['A','C'].includes(o.type)).reduce((s,o) => s + o.credit, 0),
      impayes:     all.filter(o => o.type === 'B').length,
      frais:       all.filter(o => o.type === 'D' || o.type === 'D_PREL').reduce((s,o) => s + o.debit, 0),
      depenses:    all.filter(o => o.type === 'E').reduce((s,o) => s + o.debit, 0),
      solde:       all.reduce((s,o) => s + o.credit - o.debit, 0),
    };
  }, [ops, existingOps]);

  const filtered = useMemo(() =>
    filter === 'all' ? ops
    : filter === 'D' ? ops.filter(o => o.type === 'D' || o.type === 'D_PREL')
    : ops.filter(o => o.type === filter),
    [ops, filter]);

  const pointesCount = ops.filter(o => o.pointe).length;

  return (
    <div>
      {/* Month selector */}
      <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:16 }}>
        <button className="btn btn-outline btn-sm" style={{ padding:'4px 10px', flexShrink:0 }}
          disabled={activeMois <= 1}
          onClick={() => { setActiveMois(prev => prev - 1); setOps([]); setValidated(''); setPendingFile(null); }}>◀</button>
        <div style={{ display:'flex', gap:4, flexWrap:'wrap', flex:1 }}>
          {MOIS_L.map((ml, i) => {
            const hasData = !!(comptabilite[selectedYear]?.[i+1]?.operations?.length);
            return (
              <button key={i}
                className={`btn btn-sm ${activeMois === i+1 ? 'btn-primary' : 'btn-outline'}`}
                style={{ fontSize:11, position:'relative' }}
                onClick={() => { setActiveMois(i+1); setOps([]); setValidated(''); setPendingFile(null); }}>
                {ml.slice(0,3)}
                {hasData && <span style={{ position:'absolute', top:-4, right:-4, width:8, height:8, background:'#166534', borderRadius:'50%' }} />}
              </button>
            );
          })}
        </div>
        <button className="btn btn-outline btn-sm" style={{ padding:'4px 10px', flexShrink:0 }}
          disabled={activeMois >= 12}
          onClick={() => { setActiveMois(prev => prev + 1); setOps([]); setValidated(''); setPendingFile(null); }}>▶</button>
      </div>

      {error    && <div className="alert alert-danger mb-3"><span>❌</span><div>{error}</div></div>}
      {validated && <div className="alert alert-success mb-3"><span>✅</span><div>{validated}</div></div>}
      {coherence && (
        Math.abs(coherence.ecart) < 0.02
          ? <div className="alert alert-success mb-3" style={{ fontSize:13 }}>
              <span>✅</span>
              <div>
                <strong>Import cohérent</strong> — solde calculé {formatMontant(coherence.soldeCalc)} = solde BFC {formatMontant(coherence.soldeFinal)}
                <span style={{ fontSize:11, color:'#6b7280', marginLeft:8 }}>Débits importés : {formatMontant(coherence.totalDebitsCalc)} · Crédits : {formatMontant(coherence.totalCreditsCalc)}</span>
              </div>
            </div>
          : <div className="alert alert-warning mb-3" style={{ fontSize:13 }}>
              <span>⚠️</span>
              <div>
                <strong>Écart détecté : {formatMontant(Math.abs(coherence.ecart))}</strong> — solde calculé {formatMontant(coherence.soldeCalc)} ≠ solde BFC {formatMontant(coherence.soldeFinal)}<br/>
                <span style={{ fontSize:11 }}>Débits importés : {formatMontant(coherence.totalDebitsCalc)} · Crédits : {formatMontant(coherence.totalCreditsCalc)} · Solde ouverture : {formatMontant(coherence.soldeOuverture)}</span><br/>
                <span style={{ fontSize:11, color:'#92400e' }}>Vérifiez que toutes les lignes ont été importées — notamment EXT FRAIS REMISE PREL et FRAIS IMPAYE PRELEVEMENT.</span>
              </div>
            </div>
      )}
      {pendingFile && (
        <div className="alert alert-warning mb-3">
          <span>⚠️</span>
          <div>
            <strong>Ce mois contient déjà {existingOps.length} opération(s) enregistrée(s)</strong>
            {monthInfo?.dateImport ? ` — importées le ${new Date(monthInfo.dateImport).toLocaleDateString('fr-FR')}` : ''}.
            Remplacer ces données ?
            <div style={{ display:'flex', gap:8, marginTop:8 }}>
              <button className="btn btn-outline btn-sm" onClick={() => setPendingFile(null)}>Annuler</button>
              <button className="btn btn-primary btn-sm" onClick={() => { clearComptaMoisOperations && clearComptaMoisOperations(selectedYear, activeMois); processPDF(pendingFile); setPendingFile(null); }}>
                Remplacer quand même →
              </button>
            </div>
          </div>
        </div>
      )}

      {monthInfo?.importedFile && !ops.length && (
        <div className="alert alert-info mb-3" style={{ fontSize:12 }}>
          📄 Relevé importé : <strong>{monthInfo.importedFile}</strong> · {new Date(monthInfo.dateImport).toLocaleDateString('fr-FR')}
        </div>
      )}

      {/* KPI */}
      {(ops.length > 0 || existingOps.length > 0) && (
        <div className="grid-3 mb-4" style={{ gap:12 }}>
          {[
            { label:'Cotisations reçues (VIR+PRLV)', val: formatMontant(kpi.cotisations), color:'#166534' },
            { label:'Impayés détectés',               val: kpi.impayes,                    color:'#991b1b' },
            { label:'Frais bancaires',                val: formatMontant(kpi.frais),        color:'#d97706' },
            { label:'Dépenses',                       val: formatMontant(kpi.depenses),     color:'#7c3aed' },
            { label:'Mouvement net',                  val: formatMontant(kpi.solde),        color: kpi.solde >= 0 ? '#166534' : '#991b1b' },
            ...(soldeAffiche !== null ? [{ label:`Solde au ${activeMois.toString().padStart(2,'0')}/${selectedYear}`, val: formatMontant(soldeAffiche), color:'#0369a1' }] : []),
          ].map(k => (
            <div key={k.label} className="stat-card" style={{ borderTopColor: k.color }}>
              <div className="stat-label">{k.label}</div>
              <div className="stat-value" style={{ color: k.color }}>{k.val}</div>
            </div>
          ))}
        </div>
      )}

      {/* Import zone */}
      <div className="card mb-4">
        <div className="card-header">
          <div className="card-title">📄 Import relevé BFC — {MOIS_L[activeMois-1]} {selectedYear}</div>
          <span style={{ fontSize:11, color:'#9ca3af' }}>Format BFC OI, 100 % local</span>
        </div>
        <div
          style={{ border:`2px dashed ${isDrag ? '#CC0000' : '#e5e7eb'}`, borderRadius:10,
            padding:'24px 20px', textAlign:'center', cursor: processing ? 'default' : 'pointer',
            background: isDrag ? '#fff5f5' : '#fafafa', transition:'all 0.2s' }}
          onDragOver={e => { e.preventDefault(); setIsDrag(true); }}
          onDragLeave={() => setIsDrag(false)}
          onDrop={handleDrop}
          onClick={() => !processing && fileRef.current?.click()}>
          {processing ? (
            <div style={{ color:'#CC0000', fontWeight:600 }}>⏳ Extraction et classification en cours…</div>
          ) : ops.length > 0 ? (
            <div style={{ color:'#6b7280', fontSize:13 }}>🔄 Cliquez pour importer un autre relevé</div>
          ) : (
            <div style={{ color:'#9ca3af' }}>
              <div style={{ fontSize:32, marginBottom:6 }}>📋</div>
              <div style={{ fontWeight:600, color:'#374151', fontSize:14, marginBottom:4 }}>Glissez le relevé BFC (PDF) ici</div>
              <div style={{ fontSize:11 }}>ou cliquez pour sélectionner · Aucune donnée transmise à l'extérieur</div>
            </div>
          )}
        </div>
        <input ref={fileRef} type="file" accept=".pdf" style={{ display:'none' }} onChange={handleFileChange} />
      </div>

      {/* Existing ops summary */}
      {existingOps.length > 0 && !ops.length && (
        <div className="card mb-4">
          <div className="card-header">
            <div className="card-title">📋 Opérations enregistrées — {MOIS_L[activeMois-1]}</div>
            <span style={{ fontSize:12, color:'#6b7280' }}>{existingOps.length} opération(s)</span>
          </div>
          <ExistingOpsTable ops={existingOps} adherents={adherents} />
        </div>
      )}

      {/* Results table */}
      {ops.length > 0 && (
        <div className="card">
          <div className="card-header" style={{ flexWrap:'wrap', gap:8 }}>
            <div className="card-title">📊 Résultats — {ops.length} opération(s) détectée(s)</div>
            <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
              {FILTER_OPTIONS.map(f => (
                <button key={f.key}
                  className={`btn btn-sm ${filter === f.key ? 'btn-primary' : 'btn-outline'}`}
                  style={{ fontSize:11 }}
                  onClick={() => setFilter(f.key)}>
                  {f.label} ({f.key === 'all' ? ops.length : f.key === 'D' ? ops.filter(o => o.type === 'D' || o.type === 'D_PREL').length : ops.filter(o => o.type === f.key).length})
                </button>
              ))}
            </div>
          </div>

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width:80 }}>Date</th>
                  <th>Libellé BFC</th>
                  <th style={{ width:100 }}>Type</th>
                  <th style={{ width:60, textAlign:'center' }}>Score</th>
                  <th style={{ minWidth:160 }}>Adhérent</th>
                  <th style={{ width:90 }}>Débit</th>
                  <th style={{ width:90 }}>Crédit</th>
                  <th style={{ minWidth:130 }}>Catégorie</th>
                  <th style={{ width:55, textAlign:'center' }}>✓</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(op => {
                  const cfg = TYPE_CONFIG[op.type];
                  const adhId = op.adherentId;
                  const adh = adherents.find(a => a.id === adhId);
                  return (
                    <tr key={op.id} style={{ background: op.validated ? '#f0fdf4' : undefined }}>
                      <td style={{ fontSize:12, color:'#6b7280', whiteSpace:'nowrap' }}>{op.dateAffichage || op.date?.slice(5)?.replace('-','/')}</td>
                      <td title={op.fullLibelle}>
                        <span style={{ fontFamily:'monospace', fontSize:11, color:'#1f2937' }}>{op.libelleBrut}</span>
                      </td>
                      <td>
                        <span style={{ background:cfg.bg, color:cfg.color, borderRadius:999, padding:'2px 8px', fontSize:10, fontWeight:700, whiteSpace:'nowrap' }}>
                          {cfg.icon} {cfg.label}
                        </span>
                      </td>
                      <td style={{ textAlign:'center' }}>
                        {['A','B'].includes(op.type) && op.matchScore > 0 ? (
                          <span style={{
                            background: op.matchScore >= 75 ? '#dcfce7' : op.matchScore >= 50 ? '#fef3c7' : '#fee2e2',
                            color: op.matchScore >= 75 ? '#166534' : op.matchScore >= 50 ? '#92400e' : '#991b1b',
                            borderRadius: 999, padding: '2px 6px', fontSize: 10, fontWeight: 700
                          }}>{op.matchScore}</span>
                        ) : <span style={{ color:'#d1d5db', fontSize:11 }}>—</span>}
                      </td>
                      <td>
                        {['A','B'].includes(op.type) ? (
                          <div style={{ display:'flex', gap:4, alignItems:'center' }}>
                            <AdherentPicker
                              value={adhId || null}
                              adherents={[...adherents].sort((a,b) => a.nom.localeCompare(b.nom))}
                              onChange={id => updateOp(op.id, { adherentId: id || null, matchStatus: id ? 'approx' : 'none', _forceSelect: false })}
                              placeholder="🔍 Rechercher…"
                            />
                            {saveAdherent && (
                              <button className="btn btn-sm"
                                style={{ background:'#f0fdf4', color:'#166534', border:'1px solid #86efac', whiteSpace:'nowrap', fontSize:10, padding:'3px 6px' }}
                                title="Créer un nouvel adhérent"
                                onClick={() => openCreateModal(op)}>➕</button>
                            )}
                          </div>
                        ) : <span style={{ fontSize:11, color:'#9ca3af' }}>—</span>}
                      </td>
                      <td style={{ textAlign:'right', color:'#991b1b', fontWeight:700, fontSize:12 }}>
                        {op.debit > 0 ? formatMontant(op.debit) : ''}
                      </td>
                      <td style={{ textAlign:'right', color:'#166534', fontWeight:700, fontSize:12 }}>
                        {op.credit > 0 ? formatMontant(op.credit) : ''}
                      </td>
                      <td>
                        <select className="form-control" style={{ fontSize:10, padding:'2px 4px' }}
                          value={op.categorie}
                          onChange={e => updateOp(op.id, { categorie: e.target.value })}>
                          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </td>
                      <td style={{ textAlign:'center' }}>
                        <input type="checkbox" checked={op.pointe}
                          style={{ accentColor:'#CC0000', width:15, height:15, cursor:'pointer' }}
                          onChange={e => updateOp(op.id, { pointe: e.target.checked })} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ display:'flex', gap:8, justifyContent:'flex-end', padding:'12px 4px 4px' }}>
            <button className="btn btn-outline btn-sm" onClick={() => setOps(p => p.map(o => ({ ...o, pointe: !!(o.adherentId || !['A','B'].includes(o.type)) })))}>
              Tout cocher
            </button>
            <button className="btn btn-primary btn-sm" onClick={handleValidate} disabled={pointesCount === 0}>
              ✅ Valider et comptabiliser {pointesCount > 0 ? `(${pointesCount})` : ''}
            </button>
          </div>
        </div>
      )}

      {/* Modale Créer adhérent */}
      {createModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.5)', zIndex:1200, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}
          onClick={() => setCreateModal(null)}>
          <div style={{ background:'#fff', borderRadius:14, padding:28, maxWidth:420, width:'100%', boxShadow:'0 8px 32px rgba(0,0,0,.2)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight:700, fontSize:16, marginBottom:4 }}>👤 Nouvel adhérent</div>
            <div style={{ fontSize:12, color:'#6b7280', marginBottom:18 }}>Créez l’adhérent et liez-le à cette opération en une seule fois.</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:12 }}>
              <div>
                <label style={{ fontSize:11, color:'#6b7280', fontWeight:600 }}>NOM</label>
                <input className="form-control" style={{ marginTop:4, textTransform:'uppercase' }}
                  value={createModal.nom}
                  onChange={e => setCreateModal(m => ({ ...m, nom: e.target.value.toUpperCase() }))} />
              </div>
              <div>
                <label style={{ fontSize:11, color:'#6b7280', fontWeight:600 }}>Prénom</label>
                <input className="form-control" style={{ marginTop:4 }}
                  value={createModal.prenom}
                  onChange={e => setCreateModal(m => ({ ...m, prenom: e.target.value }))} />
              </div>
            </div>
            <div style={{ marginBottom:12 }}>
              <label style={{ fontSize:11, color:'#6b7280', fontWeight:600 }}>Etablissement</label>
              <select className="form-control" style={{ marginTop:4 }}
                value={createModal.etabId}
                onChange={e => setCreateModal(m => ({ ...m, etabId: e.target.value }))}>
                <option value="">— Aucun / inconnu —</option>
                {(etablissements || []).map(e => (
                  <option key={e.id} value={e.id}>{e.nom}</option>
                ))}
              </select>
            </div>
            <div style={{ marginBottom:20 }}>
              <label style={{ fontSize:11, color:'#6b7280', fontWeight:600 }}>Montant cotisation mensuelle (€)</label>
              <input className="form-control" type="number" min="0" step="0.01" style={{ marginTop:4 }}
                value={createModal.montant}
                onChange={e => setCreateModal(m => ({ ...m, montant: e.target.value }))} />
            </div>
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <button className="btn btn-outline btn-sm" onClick={() => setCreateModal(null)}>Annuler</button>
              <button className="btn btn-primary btn-sm"
                disabled={!createModal.nom.trim()}
                onClick={handleCreateAdherent}>
                ✅ Créer et lier à l’opération
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Small table for existing ops ──
function ExistingOpsTable({ ops, adherents }) {
  return (
    <div className="table-wrap">
      <table>
        <thead><tr><th>Date</th><th>Libellé</th><th>Type</th><th>Débit</th><th>Crédit</th><th>Statut</th></tr></thead>
        <tbody>
          {ops.map(op => {
            const cfg = TYPE_CONFIG[op.type] || TYPE_CONFIG['E'];
            return (
              <tr key={op.id}>
                <td style={{ fontSize:12, color:'#6b7280' }}>{op.dateAffichage || op.date?.slice(5)?.replace('-','/')}</td>
                <td style={{ fontSize:12, fontFamily:'monospace' }}>{op.libelleBrut}</td>
                <td><span style={{ background:cfg.bg, color:cfg.color, borderRadius:999, padding:'1px 7px', fontSize:10, fontWeight:700 }}>{cfg.label}</span></td>
                <td style={{ textAlign:'right', color:'#991b1b', fontSize:12 }}>{op.debit > 0 ? formatMontant(op.debit) : ''}</td>
                <td style={{ textAlign:'right', color:'#166534', fontSize:12 }}>{op.credit > 0 ? formatMontant(op.credit) : ''}</td>
                <td><span style={{ fontSize:10, color: op.validated ? '#166534' : '#d97706' }}>{op.validated ? '✅ Validé' : '⏳ En attente'}</span></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const MOIS_L = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
